import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Observable, of, tap, catchError, finalize } from 'rxjs';
import { CdRequestService } from 'src/app/services/cd-request.service';
import { NotificationService } from 'src/app/services/notification.service';
import { toBoolOrUndefined } from 'src/utils';
import { CdRequestWizardBaseComponent } from '../cd-request-wizard-base/cd-request-wizard-base.component';
import {
  CorrHeaderComponent,
  CorrHeaderFormData,
} from './steps/corr-header/corr-header.component';
import {
  IcdPropertyInformationComponent,
  PropertyInformationFormData,
} from 'src/app/modules/cd-request/components/corr-icd-wizard/steps/property-information/icd-property-information.component';
import {
  OriginationFeesComponent,
  OriginationFeesFormData,
} from './steps/origination-fees/origination-fees.component';
import {
  ServicesCanShopComponent,
  ServicesCanShopFormData,
} from './steps/services-can-shop/services-can-shop.component';
import {
  ServicesCannotShopComponent,
  ServicesCannotShopFormData,
} from './steps/services-cannot-shop/services-cannot-shop.component';
import {
  LenderCreditsFeesComponent,
  LenderCreditsFeesFormData,
} from './steps/lender-credits-fees/lender-credits-fees.component';
import { ContactInformationComponent } from './steps/contact-information/contact-information.component';
import {
  ReviewSubmitComponent,
  ReviewSubmitFormData,
} from './steps/review-submit/review-submit.component';
import {
  ClosingDisclosureDocRequestFormModel,
  ClosingDisclosureFlowType,
  BrokerInformationSection,
  PropertyInformationSection,
  LoanInformationSection,
  FeesSection,
  CdContactInfo,
  TitleInfo,
  CorrespondentOnlySection,
  CdPropertyType,
} from 'src/app/cd-request/models/closing-disclosure-doc-request.model';
import { SellerAgentFormData } from '../wholesale-cd-wizard/steps/seller-agent/seller-agent.component';
import { TitleInformationFormData } from '../wholesale-cd-wizard/steps/title-information/title-information.component';
import { EscrowInformationFormData } from '../wholesale-cd-wizard/steps/escrow-information/escrow-information.component';
import { SettlementAgentFormData } from '../wholesale-cd-wizard/steps/settlement-agent/settlement-agent.component';

export interface ContactInformationFormData {
  sellerAgent: SellerAgentFormData | null;
  titleInformation: TitleInformationFormData | null;
  escrowInformation: EscrowInformationFormData | null;
  settlementAgent: SettlementAgentFormData | null;
}

export interface CorrIcdWizardFormData {
  flowType: ClosingDisclosureFlowType;
  corrHeader: CorrHeaderFormData | null;
  propertyInformation: PropertyInformationFormData | null;
  originationFees: OriginationFeesFormData | null;
  servicesCanShop: ServicesCanShopFormData | null;
  servicesCannotShop: ServicesCannotShopFormData | null;
  lenderCreditsFees: LenderCreditsFeesFormData | null;
  contactInformation: ContactInformationFormData | null;
  reviewSubmit: ReviewSubmitFormData | null;
}

@Component({
  selector: 'corr-icd-wizard',
  templateUrl: './corr-icd-wizard.component.html',
  styleUrls: ['./corr-icd-wizard.component.scss'],
  standalone: false,
})
export class CorrIcdWizardComponent
  extends CdRequestWizardBaseComponent
  implements OnInit
{
  @ViewChild('corrHeaderStep')
  corrHeaderStep: CorrHeaderComponent;

  @ViewChild('propertyWarehouseStep')
  propertyInformationStep: IcdPropertyInformationComponent;

  @ViewChild('originationFeesStep')
  originationFeesStep: OriginationFeesComponent;

  @ViewChild('servicesCanShopStep')
  servicesCanShopStep: ServicesCanShopComponent;

  @ViewChild('servicesCannotShopStep')
  servicesCannotShopStep: ServicesCannotShopComponent;

  @ViewChild('lenderCreditsFeesStep')
  lenderCreditsFeesStep: LenderCreditsFeesComponent;

  @ViewChild('contactInformationStep')
  contactInformationStep: ContactInformationComponent;

  @ViewChild('reviewSubmitStep')
  reviewSubmitStep: ReviewSubmitComponent;

  steps: MenuItem[] = [];
  wizardTitle: string = 'Correspondent CD Request';

  formModel: ClosingDisclosureDocRequestFormModel | null = null;
  isLoadingData: boolean = false;

  /** Selected flow type for correspondent channel */
  selectedFlowType: ClosingDisclosureFlowType = ClosingDisclosureFlowType.Icd;

  /** Flow type options for the radio button selection */
  flowTypeOptions = [
    {
      label: 'ICD (Initial Closing Disclosure)',
      value: ClosingDisclosureFlowType.Icd,
    },
    {
      label: 'Doc Order (Final Closing Documents)',
      value: ClosingDisclosureFlowType.DocOrder,
    },
  ];

  /** Whether the flow type has been selected (to show the wizard steps) */
  flowTypeSelected: boolean = false;

  private lastSavedState: string | null = null;

  constructor(
    protected override readonly injector: Injector,
    private readonly _notificationService: NotificationService,
    private readonly _cdRequestService: CdRequestService
  ) {
    super(injector);
  }

  ngOnInit(): void {
  }

  /** Handle flow type selection and update wizard title */
  onFlowTypeSelected(): void {
    this.flowTypeSelected = true;
    this.updateWizardTitle();
    this.loadRequestData();
    this.initializeSteps();
  }

  /** Update wizard title based on selected flow type */
  private updateWizardTitle(): void {
    this.wizardTitle =
      this.selectedFlowType === ClosingDisclosureFlowType.Icd
        ? 'Correspondent CD Request'
        : 'Correspondent Doc Order Request';
  }

  /** Check if the current flow is ICD (for conditional field visibility) */
  get isIcdFlow(): boolean {
    return this.selectedFlowType === ClosingDisclosureFlowType.Icd;
  }

  protected loadRequestData(): void {
    const loanId = this.applicationContext?.application?.applicationId;
    if (!loanId) {
      return;
    }

    this.isLoadingData = true;

    this._cdRequestService
      .getCorrIcdRequest(loanId, this.selectedFlowType)
      .pipe(
        catchError((error) => {
          if (error?.status === 404) {
            return of(null);
          }
          console.error(`Error loading ${this.wizardTitle || 'request'} data:`, error);
          return of(null);
        }),
        finalize(() => (this.isLoadingData = false))
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.formModel = response;
            this.submissionStatus = (response as any).status || null;
            this.submittedDate = (response as any).submittedDate
              ? new Date((response as any).submittedDate)
              : null;

            // Restore flow type from saved data if available
            if (response.flowType) {
              this.selectedFlowType = response.flowType;
              this.flowTypeSelected = true;
              this.initializeSteps();
            }
          }
          // Snapshot initial state after a tick so child components populate from @Input changes
          setTimeout(() => {
            this.lastSavedState = JSON.stringify(this.collectAllFormData());
          });
        },
      });
  }

  private initializeSteps(): void {
    const propertyStepLabel = this.isIcdFlow
      ? 'Property Info'
      : 'Property & Warehouse';
    this.steps = [
      { label: 'Correspondent Info' },
      { label: propertyStepLabel },
      { label: 'Origination Fees (Section A)' },
      { label: 'Services You CAN Shop For' },
      { label: 'Services You CANNOT Shop For' },
      { label: 'Lender Credits & Other Fees' },
      { label: 'Contact Information' },
      { label: 'Review & Submit' },
    ];
  }

  get transformedSteps(): MenuItem[] {
    return this.steps.map((step, index) => ({
      ...step,
      label: index === this.activeStepIndex ? step.label : '',
    }));
  }

  validateCurrentStep(): boolean {
    switch (this.activeStepIndex) {
      case 0:
        return this.validateCorrHeaderStep();
      case 1:
        return this.validatePropertyWarehouseStep();
      case 2:
        return this.validateOriginationFeesStep();
      case 3:
        return this.validateServicesCanShopStep();
      case 4:
        return this.validateServicesCannotShopStep();
      case 5:
        return this.validateLenderCreditsFeesStep();
      case 6:
        return this.validateContactInformationStep();
      case 7:
        return this.validateReviewSubmitStep();
      default:
        return true;
    }
  }

  private validateCorrHeaderStep(): boolean {
    if (this.corrHeaderStep) {
      return this.corrHeaderStep.validateForm();
    }
    return true;
  }

  private validatePropertyWarehouseStep(): boolean {
    if (this.propertyInformationStep) {
      return this.propertyInformationStep.validateForm();
    }
    return true;
  }

  private validateOriginationFeesStep(): boolean {
    if (this.originationFeesStep) {
      return this.originationFeesStep.validateForm();
    }
    return true;
  }

  private validateServicesCanShopStep(): boolean {
    if (this.servicesCanShopStep) {
      return this.servicesCanShopStep.validateForm();
    }
    return true;
  }

  private validateServicesCannotShopStep(): boolean {
    if (this.servicesCannotShopStep) {
      return this.servicesCannotShopStep.validateForm();
    }
    return true;
  }

  private validateLenderCreditsFeesStep(): boolean {
    if (this.lenderCreditsFeesStep) {
      return this.lenderCreditsFeesStep.validateForm();
    }
    return true;
  }

  private validateContactInformationStep(): boolean {
    if (this.contactInformationStep) {
      return this.contactInformationStep.validateForm();
    }
    return true;
  }

  private validateReviewSubmitStep(): boolean {
    if (this.reviewSubmitStep) {
      return this.reviewSubmitStep.validateForm();
    }
    return true;
  }

  onStepChange(fromStep: number, toStep: number): void {}

  autoSave(): Observable<any> {
    const currentData = this.collectAllFormData();
    const currentDataJson = JSON.stringify(currentData);

    if (currentDataJson === this.lastSavedState) {
      return of(null);
    }

    const applicationId = this.applicationContext?.application?.applicationId;
    if (!applicationId) {
      return of(null);
    }

    const mappedData = this.mapFormDataToModel(currentData);

    return this._cdRequestService
      .saveCorrIcdRequestDraft(applicationId, mappedData)
      .pipe(
        tap(() => {
          this.lastSavedState = currentDataJson;
        }),
        catchError((error) => {
          console.error('Auto-save failed:', error);
          return of(null);
        })
      );
  }

  collectAllFormData(): CorrIcdWizardFormData {
    return {
      flowType: this.selectedFlowType,
      corrHeader: this.corrHeaderStep?.getFormData() || null,
      propertyInformation: this.propertyInformationStep?.getFormData() || null,
      originationFees: this.originationFeesStep?.getFormData() || null,
      servicesCanShop: this.servicesCanShopStep?.getFormData() || null,
      servicesCannotShop: this.servicesCannotShopStep?.getFormData() || null,
      lenderCreditsFees: this.lenderCreditsFeesStep?.getFormData() || null,
      contactInformation: this.contactInformationStep?.getFormData() || null,
      reviewSubmit: this.reviewSubmitStep?.getFormData() || null,
    };
  }

  protected setNextButtonLabel(): void {
    if (this.activeStepIndex === this.steps.length - 1) {
      this.nextButtonLabel = 'Submit';
    } else {
      this.nextButtonLabel = 'Next';
    }
  }

  protected handleAutoSaveError(error: any): void {
    this._notificationService.showWarning(
      'Auto-save failed. Your changes may not be saved.',
      'Warning'
    );
    console.error('Auto-save error:', error);
  }

  /** Maps raw form data (with string Yes/No values) to the backend model (with bool values) */
  mapFormDataToModel(collectedData: CorrIcdWizardFormData): Partial<ClosingDisclosureDocRequestFormModel> {
    const corrHeader = collectedData.corrHeader;
    const propertyInfoData = collectedData.propertyInformation;
    const originationFees = collectedData.originationFees;
    const servicesCanShop = collectedData.servicesCanShop;
    const servicesCannotShop = collectedData.servicesCannotShop;
    const lenderCreditsFees = collectedData.lenderCreditsFees;
    const contactInfo = collectedData.contactInformation;
    const reviewSubmit = collectedData.reviewSubmit;

    const brokerInformation: BrokerInformationSection = {
      tpoCompanyName: corrHeader?.companyName || undefined,
      tpoCompanyNmls: corrHeader?.companyLicense || undefined,
      tpoCompanyPhone: corrHeader?.phone || undefined,
      tpoCompanyFax: corrHeader?.fax || undefined,
      processorName: corrHeader?.processorName || undefined,
      processorEmail: corrHeader?.processorEmail || undefined,
      loanOfficerName: corrHeader?.loanOfficerName || undefined,
      loanOfficerEmail: corrHeader?.loanOfficerEmail || undefined
    };

    const loanInformation: LoanInformationSection = {
      loanNumber: corrHeader?.loanNumber || undefined
    };

    const correspondentOnly: CorrespondentOnlySection = {
      accountManagerEmail: corrHeader?.amEmail || undefined,
      lockExpirationDate: corrHeader?.lockExpirationDate || undefined
    };

    const propertyInformation: PropertyInformationSection = {
      streetAddress: propertyInfoData?.propertyAddress || undefined,
      city: propertyInfoData?.city || undefined,
      state: propertyInfoData?.state || undefined,
      zip: propertyInfoData?.zip || undefined,
      mersMin: propertyInfoData?.mersMin || undefined,
      propertyType: propertyInfoData?.propertyType as CdPropertyType || undefined,
      mannerTitleHeldAs: propertyInfoData?.mannerTitleHeldAs ?? undefined,
      vesting: propertyInfoData?.vesting || undefined,
      willUsePoa: toBoolOrUndefined(propertyInfoData?.willPoaBeUsed),
      willCloseInTrust: toBoolOrUndefined(propertyInfoData?.willLoanCloseInTrust),
      nonBorrowerTitleHolder: toBoolOrUndefined(propertyInfoData?.nonBorrowerTitleHolder),
      nonBorrowerTitleHolderName: propertyInfoData?.nonBorrowerName || undefined,
      nonBorrowerTitleHolderPhone: propertyInfoData?.nonBorrowerPhone || undefined,
      nonBorrowerTitleHolderEmail: propertyInfoData?.nonBorrowerEmail || undefined,
      warehouse: propertyInfoData?.warehouse || undefined,
      isLlc: toBoolOrUndefined(propertyInfoData?.isLlc)
    };

    const fees: FeesSection = {
      applicationFee: originationFees?.applicationFee ?? undefined,
      loanOriginationFee: originationFees?.loanOriginationFee ?? undefined,
      discountPointFee: originationFees?.discountPointFee ?? undefined,
      underwriterFee: originationFees?.underwriterFee ?? undefined,
      processorFee: originationFees?.processorFee ?? undefined,
      surveyFee: servicesCanShop?.surveyFee ?? undefined,
      appraisalFee: servicesCannotShop?.appraisalFee ?? undefined,
      creditReportFee: servicesCannotShop?.creditReport ?? undefined,
      interestRateCredit: lenderCreditsFees.interestRateCredit ?? undefined,
      wireFee: lenderCreditsFees?.wireFee ?? undefined,
      dynamicOriginationFees: originationFees?.dynamicFees ?? undefined,
      dynamicServicesCanShop: servicesCanShop?.dynamicFees ?? undefined,
      dynamicServicesCannotShop: servicesCannotShop?.dynamicFees ?? undefined,
      dynamicOtherFees: lenderCreditsFees?.dynamicFees ?? undefined
    };

    const sellerRealEstateAgent: CdContactInfo = {
      companyName: contactInfo?.sellerAgent?.companyName || undefined,
      companyLicenseId: contactInfo?.sellerAgent?.companyLicenseId || undefined,
      companyAddress: contactInfo?.sellerAgent?.address || undefined,
      city: contactInfo?.sellerAgent?.city || undefined,
      state: contactInfo?.sellerAgent?.state || undefined,
      zip: contactInfo?.sellerAgent?.zip || undefined,
      contactFullName: contactInfo?.sellerAgent?.contactName || undefined,
      contactLicenseId: contactInfo?.sellerAgent?.contactLicense || undefined,
      contactEmailAddress: contactInfo?.sellerAgent?.contactEmail || undefined,
      contactPhoneNumber: contactInfo?.sellerAgent?.contactPhone || undefined
    };

    const titleInformation: TitleInfo = {
      companyName: contactInfo?.titleInformation?.companyName || undefined,
      companyLicenseId: contactInfo?.titleInformation?.companyLicenseId || undefined,
      titleCaseNumber: contactInfo?.titleInformation?.titleCaseNumber || undefined,
      companyAddress: contactInfo?.titleInformation?.streetAddress || undefined,
      city: contactInfo?.titleInformation?.city || undefined,
      state: contactInfo?.titleInformation?.state || undefined,
      zip: contactInfo?.titleInformation?.zip || undefined,
      contactFullName: contactInfo?.titleInformation?.contactName || undefined,
      contactLicenseId: contactInfo?.titleInformation?.contactLicense || undefined,
      contactEmailAddress: contactInfo?.titleInformation?.contactEmail || undefined,
      contactPhoneNumber: contactInfo?.titleInformation?.contactPhone || undefined
    };

    const escrowInformation: CdContactInfo = {
      companyName: contactInfo?.escrowInformation?.companyName || undefined,
      companyLicenseId: contactInfo?.escrowInformation?.companyLicenseId || undefined,
      companyAddress: contactInfo?.escrowInformation?.address || undefined,
      city: contactInfo?.escrowInformation?.city || undefined,
      state: contactInfo?.escrowInformation?.state || undefined,
      zip: contactInfo?.escrowInformation?.zip || undefined,
      contactFullName: contactInfo?.escrowInformation?.contactName || undefined,
      contactLicenseId: contactInfo?.escrowInformation?.contactLicense || undefined,
      contactEmailAddress: contactInfo?.escrowInformation?.contactEmail || undefined,
      contactPhoneNumber: contactInfo?.escrowInformation?.contactPhone || undefined
    };

    const settlementInformation: CdContactInfo = {
      companyName: contactInfo?.settlementAgent?.companyName || undefined,
      companyLicenseId: contactInfo?.settlementAgent?.companyLicenseId || undefined,
      companyAddress: contactInfo?.settlementAgent?.address || undefined,
      city: contactInfo?.settlementAgent?.city || undefined,
      state: contactInfo?.settlementAgent?.state || undefined,
      zip: contactInfo?.settlementAgent?.zip || undefined,
      contactFullName: contactInfo?.settlementAgent?.contactName || undefined,
      contactLicenseId: contactInfo?.settlementAgent?.contactLicense || undefined,
      contactEmailAddress: contactInfo?.settlementAgent?.contactEmail || undefined,
      contactPhoneNumber: contactInfo?.settlementAgent?.contactPhone || undefined
    };

    return {
      applicationId: this.applicationContext?.application?.applicationId ?? undefined,
      showCorrespondentOnlyFields: true,
      flowType: collectedData.flowType,
      additionalComments: reviewSubmit?.additionalComments || undefined,
      brokerInformation,
      propertyInformation,
      loanInformation,
      fees,
      sellerRealEstateAgent,
      titleInformation,
      escrowInformation,
      settlementInformation,
      correspondentOnly
    };
  }
}
